import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Flame,
  Trophy,
  Ticket,
  MapPin,
  ArrowUpRight,
  Activity,
} from "lucide-react";
import { RevealText, FadeIn } from "../components/RevealText";
import { EditorialMarquee } from "../components/EditorialMarquee";

const HERO_IMG = "https://images.unsplash.com/photo-1485872299829-c673f5194813";
const CROWD_IMG = "https://images.unsplash.com/photo-1656450552598-2445a0f1480d";
const RESTAURANT_IMG =
  "https://images.unsplash.com/photo-1524237964353-f14cde1db034";

const CHAPTERS = [
  {
    n: "01",
    title: "See the vibe before you go",
    body: "Live busyness meters show which bars and restaurants near campus are packed, warming up, or dead quiet — so you never waste a walk across town.",
  },
  {
    n: "02",
    title: "Real deals, real savings",
    body: "Student-only discounts and happy-hour drops from local spots. saveu puts money back in your pocket every time you go out.",
  },
  {
    n: "03",
    title: "Every night earns points",
    body: "Check in, redeem deals, and rack up points automatically. The more you explore campus nightlife, the more you stack.",
  },
  {
    n: "04",
    title: "Climb the campus leaderboard",
    body: "Compete with friends and your whole school. Top the leaderboard, unlock perks, and earn campus-wide bragging rights.",
  },
];

const FEATURES = [
  {
    icon: Activity,
    title: "Live Busyness",
    desc: "Real-time crowd meters for every bar & restaurant.",
    span: "md:col-span-7",
    accent: true,
  },
  {
    icon: Ticket,
    title: "Student Deals",
    desc: "Exclusive discounts you won't find anywhere else.",
    span: "md:col-span-5",
  },
  {
    icon: Trophy,
    title: "Leaderboard",
    desc: "Rank up against your friends and your campus.",
    span: "md:col-span-5",
  },
  {
    icon: Flame,
    title: "Points System",
    desc: "Turn every night out into rewards worth chasing.",
    span: "md:col-span-7",
    accent: true,
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex flex-col justify-center px-6 lg:px-10 pt-28 pb-20 overflow-hidden"
      >
        {/* ambient glow */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 0.4 }}
          className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-brand/25 blur-3xl"
        />
        <div className="mx-auto max-w-7xl w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="font-body uppercase tracking-[0.4em] text-xs text-brand-dark mb-8"
            >
              The Campus Nightlife App
            </motion.p>
            <RevealText
              as="h1"
              lines={["Know where", "the night is", "actually alive."]}
              className="font-display font-black uppercase tracking-tighter text-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
              delay={0.3}
            />
            <FadeIn delay={1.1}>
              <p className="mt-8 max-w-lg text-lg text-ink-soft leading-relaxed">
                saveu shows how busy every bar and restaurant near your campus
                is in real time, unlocks student deals, and rewards you with
                points and a leaderboard for going out.
              </p>
            </FadeIn>
            <FadeIn delay={1.3}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/support"
                  data-testid="hero-support-btn"
                  className="glow-ring bg-brand text-white font-body font-semibold uppercase tracking-wider px-8 py-4 rounded-full hover:-translate-y-1 transition-transform flex items-center gap-2"
                >
                  Get Support <ArrowUpRight size={18} />
                </Link>
                <Link
                  to="/faq"
                  data-testid="hero-faq-btn"
                  className="border border-ink/20 text-ink font-body font-semibold uppercase tracking-wider px-8 py-4 rounded-full hover:border-brand hover:text-brand transition-colors"
                >
                  Read FAQ
                </Link>
              </div>
            </FadeIn>
          </div>

          {/* parallax clipped hero image */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative h-[420px] lg:h-[560px] overflow-hidden rounded-[32px] border border-ink/10 shadow-2xl"
            >
              <motion.img
                src={HERO_IMG}
                alt="Friends out at a busy campus bar"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 w-full h-[130%] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/85 backdrop-blur-md rounded-2xl p-5 flex items-center gap-4">
                <div className="w-3 h-3 rounded-full bg-brand animate-pulse" />
                <div>
                  <p className="font-display font-bold text-ink text-sm uppercase tracking-wide">
                    O'Malley's — Packed
                  </p>
                  <p className="text-ink-soft text-sm">92% busy · 2 deals live</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* ---------- OVERVIEW / BENTO ---------- */}
      <section className="px-6 lg:px-10 py-32">
        <div className="mx-auto max-w-7xl">
          <FadeIn>
            <p className="font-body uppercase tracking-[0.4em] text-xs text-brand-dark mb-6">
              What saveu does
            </p>
            <h2 className="font-display font-extrabold uppercase tracking-tighter text-ink text-3xl md:text-5xl max-w-2xl leading-tight">
              One app for the whole night out.
            </h2>
          </FadeIn>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
            {FEATURES.map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.08} className={f.span}>
                <div
                  data-testid={`feature-card-${i}`}
                  className={`group h-full rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1 ${
                    f.accent
                      ? "bg-brand text-white border-brand"
                      : "bg-white border-ink/10 hover:border-brand"
                  }`}
                >
                  <f.icon
                    size={34}
                    className={f.accent ? "text-white" : "text-brand"}
                  />
                  <h3 className="mt-8 font-display font-bold text-2xl tracking-tight">
                    {f.title}
                  </h3>
                  <p
                    className={`mt-3 leading-relaxed ${
                      f.accent ? "text-white/80" : "text-ink-soft"
                    }`}
                  >
                    {f.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- MANIFESTO CHAPTERS ---------- */}
      <section className="px-6 lg:px-10 py-32 bg-white border-y border-ink/10">
        <div className="mx-auto max-w-6xl">
          <FadeIn>
            <h2 className="font-display font-extrabold uppercase tracking-tighter text-ink text-3xl md:text-5xl mb-24 max-w-3xl leading-tight">
              How it works, chapter by chapter.
            </h2>
          </FadeIn>

          <div className="space-y-24">
            {CHAPTERS.map((c, i) => (
              <FadeIn key={c.n} delay={0.05}>
                <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                  <span className="md:col-span-3 font-display font-black text-6xl md:text-7xl text-ink/10 leading-none select-none">
                    {c.n}
                  </span>
                  <div className="md:col-span-9 md:pl-8">
                    <div className="flex items-center gap-3 mb-4">
                      <MapPin size={18} className="text-brand" />
                      <span className="w-16 h-px bg-brand" />
                    </div>
                    <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight text-ink max-w-xl">
                      {c.title}
                    </h3>
                    <p className="mt-4 text-lg text-ink-soft leading-relaxed max-w-xl">
                      {c.body}
                    </p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- IMAGE STRIP ---------- */}
      <section className="px-6 lg:px-10 py-24">
        <div className="mx-auto max-w-7xl grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { img: CROWD_IMG, label: "The leaderboard crowd" },
            { img: RESTAURANT_IMG, label: "Every spot, live" },
          ].map((item, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div className="relative h-[340px] overflow-hidden rounded-3xl border border-ink/10 group">
                <img
                  src={item.img}
                  alt={item.label}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
                <p className="absolute bottom-6 left-6 font-display font-bold uppercase tracking-tight text-white text-xl">
                  {item.label}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="px-6 lg:px-10 py-32">
        <div className="mx-auto max-w-5xl text-center relative overflow-hidden rounded-[40px] bg-brand px-8 py-24">
          <div className="pointer-events-none absolute -bottom-32 -right-20 w-[400px] h-[400px] rounded-full bg-white/20 blur-3xl" />
          <FadeIn>
            <h2 className="font-display font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.95]">
              Need a hand?
            </h2>
            <p className="mt-6 text-white/85 text-lg max-w-xl mx-auto">
              Questions, feedback, or account help — the saveu team is one
              message away.
            </p>
            <Link
              to="/support"
              data-testid="cta-support-btn"
              className="mt-10 inline-flex items-center gap-2 bg-white text-brand-dark font-body font-bold uppercase tracking-wider px-10 py-4 rounded-full hover:-translate-y-1 transition-transform"
            >
              Contact Support <ArrowUpRight size={18} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
